import { DashboardCard } from "@dashboard/components/Card";
import CardSpacer from "@dashboard/components/CardSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Box, Button, Checkbox, Input, Text, TrashBinIcon } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

export interface CheckoutQuestion {
  id: string;
  question: string;
  required: boolean;
  hasMultipleChoice: boolean;
  options?: string[];
}

export interface CheckoutQuestionsFormData {
  reCAPTCHA: boolean;
  guest_only: boolean;
  order_value: string;
  questions: CheckoutQuestion[];
  isActive: boolean;
}

export interface SimpleCheckoutQuestionsPageProps {
  disabled: boolean;
  errors: string[];
  onSubmit: (data: CheckoutQuestionsFormData) => Promise<void>;
  initialData?: CheckoutQuestionsFormData;
}

const generateId = () => Math.random().toString(36).substr(2, 9);

export const SimpleCheckoutQuestionsPage: React.FC<SimpleCheckoutQuestionsPageProps> = ({
  disabled,
  errors,
  onSubmit,
  initialData,
}) => {
  const [formData, setFormData] = useState<CheckoutQuestionsFormData>(
    initialData || {
      reCAPTCHA: false,
      guest_only: false,
      order_value: "",
      questions: [],
      isActive: false,
    }
  );

  const [collapsedStates, setCollapsedStates] = useState<Record<string, boolean>>({});

  const handleAddQuestion = () => {
    const newQuestion: CheckoutQuestion = {
      id: generateId(),
      question: "",
      required: false,
      hasMultipleChoice: true,
      options: ["Option 1", "Option 2"],
    };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion], // Add to the end (bottom)
    }));
    // Auto-expand the new question
    setCollapsedStates(prev => ({
      ...prev,
      [newQuestion.id]: false,
    }));
  };

  const handleDeleteQuestion = (questionId: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId),
    }));
  };

  const handleQuestionChange = (questionId: string, field: keyof CheckoutQuestion, value: any) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      ),
    }));
  };

  const handleOptionChange = (questionId: string, optionIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId && q.options) {
          const newOptions = [...q.options];

          newOptions[optionIndex] = value;

          return { ...q, options: newOptions };
        }

        return q;
      }),
    }));
  };

  const handleAddOption = (questionId: string) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId && q.options) {
          return { ...q, options: [...q.options, "New Option"] };
        }

        return q;
      }),
    }));
  };

  const toggleCollapsed = (questionId: string) => {
    setCollapsedStates(prev => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <DetailPageLayout>
      <DetailPageLayout.Content>
        <Box paddingX={8} paddingY={6} >
          <DashboardCard>
            <DashboardCard.Header>
              <DashboardCard.Title>
                💬 Checkout Questions Configuration
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Text color="default2" size={3}>
                Configure checkout questions that customers will see during the checkout process. Add custom questions to collect additional information from your customers.
              </Text>
            </DashboardCard.Content>
          </DashboardCard>
          <CardSpacer />

          {errors.length > 0 && (
            <>
              <Box
                backgroundColor="critical1"
                color="default1"
                padding={4}
                borderRadius={2}
                marginBottom={4}
              >
                {errors.map((error, index) => (
                  <Text key={index} color="default1">{error}</Text>
                ))}
              </Box>
              <CardSpacer />
            </>
          )}

          <form id="checkout-questions-form" onSubmit={handleSubmit}>
            <DashboardCard>
              <DashboardCard.Header>
                <DashboardCard.Title>Settings</DashboardCard.Title>
              </DashboardCard.Header>
              <DashboardCard.Content>
                <Box display="flex" flexDirection="column" gap={4}>
                  {/* Active/Inactive Toggle */}
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between"
                    gap={3} 
                    padding={4} 
                    backgroundColor="default2"
                    borderRadius={2}
                    borderStyle="solid"
                    borderWidth={1}
                    borderColor="default1"
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Text size={4} color="default1">
                        <strong>Checkout Questions:</strong>
                      </Text>
                      <Text size={4} color={formData.isActive ? "info1" : "defaultDisabled"}>
                        {formData.isActive ? "🟢 Enabled" : "⚪ Disabled"}
                      </Text>
                    </Box>
                    <Checkbox
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked === true }))}
                      disabled={disabled}
                    />
                  </Box>

                  <Checkbox
                    checked={formData.reCAPTCHA}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, reCAPTCHA: checked === true }))}
                    disabled={disabled}
                  >
                    <Text>Enable reCAPTCHA</Text>
                  </Checkbox>

                  <Checkbox
                    checked={formData.guest_only}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, guest_only: checked === true }))}
                    disabled={disabled}
                  >
                    <Text>Guest Only</Text>
                  </Checkbox>

                  <Box display="flex" alignItems="center" gap={2}>
                    <Text>Order Value:</Text>
                    <Input
                      type="number"
                      value={formData.order_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, order_value: e.target.value }))}
                      disabled={disabled}
                      size="small"
                      style={{ width: "120px" }}
                    />
                  </Box>
                </Box>
              </DashboardCard.Content>
            </DashboardCard>
            <CardSpacer />

            <DashboardCard>
              <DashboardCard.Header>
                <DashboardCard.Title>
                  ❓ Questions ({formData.questions.length})
                </DashboardCard.Title>
                <Button
                  variant="primary"
                  onClick={handleAddQuestion}
                  disabled={disabled}
                >
                  ➕ Add Question
                </Button>
              </DashboardCard.Header>
              <DashboardCard.Content>

          {formData.questions.slice().reverse().map((question) => (
            <div key={question.id} style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              marginBottom: "16px"
            }}>
              {/* Question Header */}
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  borderBottom: collapsedStates[question.id] ? "none" : "1px solid #e0e0e0",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                  <input
                    type="text"
                    value={question.question}
                    onChange={(e) => handleQuestionChange(question.id, "question", e.target.value)}
                    placeholder="Enter question title..."
                    disabled={disabled}
                    style={{
                      flex: 1,
                      minWidth: "200px",
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                      backgroundColor: "white"
                    }}
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => handleDeleteQuestion(question.id)}
                    disabled={disabled}
                    title="Delete this question"
                    icon={<TrashBinIcon />}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => toggleCollapsed(question.id)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "18px",
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    color: "#666"
                  }}
                  title={collapsedStates[question.id] ? "Expand question" : "Collapse question"}
                >
                  {collapsedStates[question.id] ? "▲" : "▼"}
                </button>
              </div>

              {/* Question Body */}
              {!collapsedStates[question.id] && (
                <div style={{ padding: "16px" }}>
                  {/* Question Controls */}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "24px", 
                    marginBottom: "20px",
                    padding: "12px 16px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef"
                  }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(e) => handleQuestionChange(question.id, "required", e.target.checked)}
                        disabled={disabled}
                        style={{ transform: "scale(1.1)" }}
                      />
                      <span style={{ fontWeight: 500, color: "#495057" }}>Required</span>
                    </label>

                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={question.hasMultipleChoice}
                        onChange={(e) => handleQuestionChange(question.id, "hasMultipleChoice", e.target.checked)}
                        disabled={disabled}
                        style={{ transform: "scale(1.1)" }}
                      />
                      <span style={{ fontWeight: 500, color: "#495057" }}>Multiple Choice</span>
                    </label>
                  </div>

                  {/* Conditional Content based on Multiple Choice toggle */}
                  {question.hasMultipleChoice ? (
                    /* Multiple Choice Options */
                    <div>
                      <p style={{ fontWeight: 500, marginBottom: "8px" }}>
                        Multiple choice options:
                      </p>
                      <p style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}>
                        Configure the available options for this multiple choice question.
                      </p>

                      {question.options?.map((option, optionIndex) => (
                        <div key={optionIndex} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <span style={{ minWidth: "60px" }}>Option {String.fromCharCode(97 + optionIndex)}</span>
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => handleOptionChange(question.id, optionIndex, e.target.value)}
                            disabled={disabled}
                            style={{
                              minWidth: "200px",
                              padding: "4px 8px",
                              border: "1px solid #ccc",
                              borderRadius: "4px"
                            }}
                          />
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => handleAddOption(question.id)}
                        disabled={disabled}
                        style={{
                          backgroundColor: "#6c757d",
                          color: "white",
                          padding: "4px 12px",
                          border: "none",
                          borderRadius: "4px",
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontSize: "12px",
                          marginTop: "8px"
                        }}
                      >
                        Add more answers
                      </button>
                    </div>
                  ) : (
                    /* Simple Text Input */
                    <div>
                      <p style={{ fontWeight: 500, marginBottom: "8px" }}>
                        Text input question:
                      </p>
                      <p style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}>
                        This question will appear as a simple text input field for customers to fill in.
                      </p>
                      <div style={{ 
                        padding: "12px", 
                        backgroundColor: "#f5f5f5", 
                        borderRadius: "4px",
                        border: "1px solid #ddd"
                      }}>
                        <p style={{ margin: 0, fontStyle: "italic", color: "#666" }}>
                          Preview: Customers will see a text input box to type their answer.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {formData.questions.length === 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "32px",
              color: "#666",
              fontStyle: "italic"
            }}>
              No questions added yet. Click &quot;Add question&quot; to get started.
            </div>
          )}
              </DashboardCard.Content>
            </DashboardCard>

            {/* Spacer to prevent content from being hidden behind sticky buttons */}
            <div style={{ height: "80px" }}></div>
          </form>

      {/* Sticky Submit Buttons */}
      <div style={{ 
        position: "fixed",
        bottom: 0,
        left: "262px", // Account for sidebar width
        right: 0,
        backgroundColor: "white",
        borderTop: "1px solid #e0e0e0",
        padding: "12px",
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
        boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 1000
      }}>
        <button
          type="button"
          style={{
            padding: "10px 20px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "white",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="checkout-questions-form"
          disabled={disabled}
          style={{
            padding: "10px 20px",
            border: "none",
            borderRadius: "4px",
            backgroundColor: disabled ? "#ccc" : "#1976d2",
            color: "white",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: 500
          }}
        >
          {disabled ? "Saving..." : "Save"}
        </button>
      </div>
        </Box>
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};